import React, { FC, useEffect, useState } from "react";
import { siteCoreContentLoadingSelector } from "../../../../redux/slices/appState/appStateSelector";
import { useSelector } from "react-redux";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { useForm, useWatch } from "react-hook-form";
import {
  isValueIncludedIgnoreCase,
  getValueOrBlank,
  isNotEmpty,
  buildActionMenuItems,
} from "../../../../helpers/utils";
import {
  FormController,
  MuiBox,
  MuiGrid,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  RowProps,
} from "../../../../components";
import { InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import {
  MAX_LENGTH_50,
  MODULE_PERMISSIONS,
} from "../../../../helpers/constants";
import { LabelValue } from "../../../../helpers/types/common";
import {
  MdmOfficeSearchResultFiltersTypes,
  MdmSearchOfficeResultFilterForm,
  MdmSearchOfficeResultFilterFormKey,
  MdmSearchOfficeResultRowKey,
  MdmSearchOfficeResultRowValue,
} from "../../modelsAndSchemas/models";
import { MdmOfficeSearchResultWrapper } from "./MdmOfficeSearchResults.style";
import { mdmOfficeResultSiteCoreKeys } from "../../../mdmCreateOffice/siteCore/SiteCoreKeys";
import { isString } from "lodash";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { usePermissions } from "../../../../hooks/usePermissions";

interface OfficeTemplateSearchResultProps {
  rows: MdmSearchOfficeResultRowValue[];
  isSearchClick: boolean;
  onEditOfficeClick: (id: string) => void;
  onDeleteOfficeClick: (id: string) => void;
  onViewOfficeClick: (id: string) => void;
}

export const MdmOfficeSearchResults: FC<OfficeTemplateSearchResultProps> = ({
  rows,
  isSearchClick,
  onDeleteOfficeClick,
  onEditOfficeClick,
  onViewOfficeClick,
}) => {
  const pageId = "mdm-office-search-results";
  const tableRef = React.useRef<{
    resetPagination: (shouldResetRowsPerPage?: boolean) => void;
  }>(null);
  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [allUpdatedRows, setAllUpdatedRows] = useState<
    MdmSearchOfficeResultRowValue[]
  >([]);
  const [uniqueFilters, setUniqueFilters] =
    useState<MdmOfficeSearchResultFiltersTypes>(() => ({
      parentOffice: [],
      officeCode: [],
      statusId: [],
      city: [],
      state: [],
      marketValue: [],
      countryId: [],
    }));

  const {
    fetchLabel,
    commonLabels,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const {
    officeResultsTitle,
    allOfficeSectionHeading,
    officeName,
    parentOffice,
    officeCode,
    officeType,
    city,
    stateOrProvince,
    market,
    country,
    status,
    action,
  } = mdmOfficeResultSiteCoreKeys;

  const getLabel = (key: string) => {
    return fetchLabel(officeResultsTitle, key);
  };

  const { control, reset } = useForm<MdmSearchOfficeResultFilterForm>({
    mode: "all",
    defaultValues: {
      [MdmSearchOfficeResultFilterFormKey.officeName]: "",
      [MdmSearchOfficeResultFilterFormKey.parentOffice]: "",
      [MdmSearchOfficeResultFilterFormKey.officeCode]: "",
      [MdmSearchOfficeResultFilterFormKey.statusId]: "",
      [MdmSearchOfficeResultFilterFormKey.officeType]: "",
      [MdmSearchOfficeResultFilterFormKey.city]: "",
      [MdmSearchOfficeResultFilterFormKey.state]: "",
      [MdmSearchOfficeResultFilterFormKey.marketValue]: "",
      [MdmSearchOfficeResultFilterFormKey.countryId]: "",
    },
  });

  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);

  const resetPagination = (shouldResetRowsPerPage: boolean = false) => {
    if (tableRef.current) {
      tableRef.current.resetPagination(shouldResetRowsPerPage);
    }
  };

  useEffect(() => {
    resetPagination(true);
    reset();
  }, [rows]);

  const getUniqueFilters = (
    data: MdmSearchOfficeResultRowValue[],
    key: string,
  ): LabelValue[] => {
    const flatValues: string[] = [];

    data.forEach((item) => {
      const value = item[key];
      if (isString(value) && isNotEmpty(value.trim())) {
        flatValues.push(value);
      }
    });

    return Array.from(new Set(flatValues)).map((v) => ({
      value: v,
      label: v,
    }));
  };

  useEffect(() => {
    if (!siteCoreLoading) {
      const localData = [...rows];

      localData.forEach((item: MdmSearchOfficeResultRowValue) => {
        item.marketValue = item.market.name;
      });

      setUniqueFilters((prevFilters) => ({
        ...prevFilters,
        [MdmSearchOfficeResultRowKey.parentOffice]: getUniqueFilters(
          localData,
          MdmSearchOfficeResultRowKey.parentOffice,
        ),
        [MdmSearchOfficeResultRowKey.officeCode]: getUniqueFilters(
          localData,
          MdmSearchOfficeResultRowKey.officeCode,
        ),
        [MdmSearchOfficeResultRowKey.statusId]: getUniqueFilters(
          localData,
          MdmSearchOfficeResultRowKey.statusId,
        ),
        [MdmSearchOfficeResultRowKey.city]: getUniqueFilters(
          localData,
          MdmSearchOfficeResultRowKey.city,
        ),
        [MdmSearchOfficeResultRowKey.state]: getUniqueFilters(
          localData,
          MdmSearchOfficeResultRowKey.state,
        ),
        [MdmSearchOfficeResultRowKey.marketValue]: getUniqueFilters(
          localData,
          MdmSearchOfficeResultRowKey.marketValue,
        ),
        [MdmSearchOfficeResultRowKey.countryId]: Array.from(
          new Set(
            localData
              .map((item) => item.country?.name)
              .filter((value) => isString(value) && isNotEmpty(value.trim())),
          ),
        ).map((v) => ({ value: v, label: v })),
      }));

      setAllUpdatedRows(localData);
      setFilteredResults([...localData]);
    }
  }, [rows, siteCoreLoading]);

  const filters = useWatch({ control });
  useEffect(() => {
    if (!allUpdatedRows.length) return;

    let filteredList = [...allUpdatedRows];

    const {
      officeName,
      parentOffice,
      officeCode,
      statusId,
      officeType,
      city,
      state,
      market,
      countryId,
    } = filters || {};

    filteredList = filteredList.filter((item) => {
      return (
        isValueIncludedIgnoreCase(
          item.officeName,
          getValueOrBlank(officeName),
        ) &&
        isValueIncludedIgnoreCase(
          item.parentOffice,
          getValueOrBlank(parentOffice),
        ) &&
        isValueIncludedIgnoreCase(
          item.officeCode,
          getValueOrBlank(officeCode),
        ) &&
        isValueIncludedIgnoreCase(
          item.officeType,
          getValueOrBlank(officeType),
        ) &&
        isValueIncludedIgnoreCase(item.city, getValueOrBlank(city)) &&
        isValueIncludedIgnoreCase(item.state, getValueOrBlank(state)) &&
        isValueIncludedIgnoreCase(item.marketValue, getValueOrBlank(market)) &&
        isValueIncludedIgnoreCase(item.statusId, getValueOrBlank(statusId)) &&
        isValueIncludedIgnoreCase(
          item.country?.name,
          getValueOrBlank(countryId),
        )
      );
    });

    setFilteredResults(filteredList);
    tableRef.current?.resetPagination();
  }, [filters]);

  const columns = [
    {
      id: MdmSearchOfficeResultRowKey.officeName,
      label: getLabel(officeName),
      isEmptyValue: true,
      isSortVisible: true,
      render: (row: RowProps) =>
        !row.officeName ? null : (
          <MuiLink
            onClick={() => onViewOfficeClick?.(row.id)}
            id={`${pageId}-view-officeName`}
            data-testid="view-officeName-link"
          >
            <MuiBox>
              <MuiEllipseLabel
                labelTypoClassName="Link"
                text={row.officeName}
                labelTypoVariant="h3"
                placement="bottom-start"
                pointerCursor
              />
            </MuiBox>
          </MuiLink>
        ),
    },
    {
      id: MdmSearchOfficeResultRowKey.parentOffice,
      label: getLabel(parentOffice),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: MdmSearchOfficeResultRowKey.officeCode,
      label: getLabel(officeCode),
      isSortVisible: true,
      isEmptyValue: true,
    },
    {
      id: MdmSearchOfficeResultRowKey.statusId,
      label: getLabel(status),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            <StatusBadge
              label={fetchValueByKeyOfDropdown(
                mdmOfficeResultSiteCoreKeys.officeResultsTitle,
                mdmOfficeResultSiteCoreKeys.resultStatusList,
                row.statusId,
              )}
              status={row.statusId}
            ></StatusBadge>
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: MdmSearchOfficeResultRowKey.officeType,
      label: getLabel(officeType),
      isSortVisible: true,
      isEmptyValue: true,
      className: "units-cell",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {fetchValueByKeyOfDropdown(
              mdmOfficeResultSiteCoreKeys.officeResultsTitle,
              mdmOfficeResultSiteCoreKeys.officeTypeList,
              row.officeType,
            )}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: MdmSearchOfficeResultRowKey.city,
      label: getLabel(city),
      isSortVisible: true,
      isEmptyValue: true,
      className: "units-cell",
    },
    {
      id: MdmSearchOfficeResultRowKey.state,
      label: getLabel(stateOrProvince),
      isSortVisible: true,
      isEmptyValue: true,
      className: "units-cell",
    },
    {
      id: MdmSearchOfficeResultRowKey.marketValue,
      label: getLabel(market),
      isSortVisible: true,
      isEmptyValue: true,
      className: "units-cell",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {row.marketValue}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: MdmSearchOfficeResultRowKey.countryId,
      label: getLabel(country),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3" className="Link">
            {row.country?.name}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: "action",
      label: getLabel(action),
      render: (row: RowProps) => {
        const menuItems = buildActionMenuItems(
          row,
          [
            {
              permissionKey: MODULE_PERMISSIONS.EDIT_MDM_OFFICE,
              create: (row) => ({
                onClick: () => onEditOfficeClick(row?.id),
                dataTestId: "edit",
                label: commonLabels.edit,
                ariaLabel: commonLabels.edit,
              }),
            },
            {
              permissionKey: MODULE_PERMISSIONS.VIEW_MDM_OFFICE,
              create: (row) => ({
                onClick: () => onViewOfficeClick(row?.id),
                dataTestId: "view",
                label: commonLabels.view,
                ariaLabel: commonLabels.view,
              }),
            },
          ],
          hasModulePermission,
        );

        return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
      },
    },
  ];

  const tableHeaderForm = [
    <FormController
      control={control}
      name={MdmSearchOfficeResultFilterFormKey.officeName}
      key={MdmSearchOfficeResultFilterFormKey.officeName}
    >
      <MuiTextField
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.officeName}`}
        startAdornment={
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        }
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={MdmSearchOfficeResultFilterFormKey.parentOffice}
      key={MdmSearchOfficeResultFilterFormKey.parentOffice}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.parentOffice}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.parentOffice}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={MdmSearchOfficeResultFilterFormKey.officeCode}
      key={MdmSearchOfficeResultFilterFormKey.officeCode}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.officeCode}
        visibleDefaultSelectOption
        value=""
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.officeCode}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={MdmSearchOfficeResultFilterFormKey.statusId}
      key={MdmSearchOfficeResultFilterFormKey.statusId}
    >
      <MuiSelect
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.statusId}`}
        options={fetchDropdownValues(
          mdmOfficeResultSiteCoreKeys.officeResultsTitle,
          mdmOfficeResultSiteCoreKeys.resultStatusList,
        )}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        label={""}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={MdmSearchOfficeResultFilterFormKey.officeType}
      name={MdmSearchOfficeResultFilterFormKey.officeType}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(
          mdmOfficeResultSiteCoreKeys.officeResultsTitle,
          mdmOfficeResultSiteCoreKeys.officeTypeList,
        )}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.officeType}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={MdmSearchOfficeResultFilterFormKey.city}
      name={MdmSearchOfficeResultFilterFormKey.city}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.city}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        onChange={() => {}}
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.city}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={MdmSearchOfficeResultFilterFormKey.state}
      name={MdmSearchOfficeResultFilterFormKey.state}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.state}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        onChange={() => {}}
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.state}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={MdmSearchOfficeResultFilterFormKey.market}
      name={MdmSearchOfficeResultFilterFormKey.market}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.marketValue}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        onChange={() => {}}
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.market}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={MdmSearchOfficeResultFilterFormKey.countryId}
      name={MdmSearchOfficeResultFilterFormKey.countryId}
    >
      <MuiSelect
        label=""
        options={uniqueFilters.countryId}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        onChange={() => {}}
        id={`${pageId}-${MdmSearchOfficeResultFilterFormKey.countryId}`}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <></>,
  ];

  return (
    <MdmOfficeSearchResultWrapper data-testid="mdm-office-search-results">
      <MuiBox className="result-header">
        <MuiTypography variant="h2SemiBold" className="header-text">
          {getLabel(allOfficeSectionHeading)}
        </MuiTypography>
        {rows.length > 0 && (
          <MuiTypography variant="h3SemiBold" className="selected-office-count">
            {rows.length}
          </MuiTypography>
        )}
      </MuiBox>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }}>
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={tableHeaderForm}
            columns={columns}
            tableResults={[...filteredResults]}
            priorityResults={[]}
            isLastColumnSticky
            noDataInApi={isSearchClick}
            isShowDashIfValueEmpty
          />
        </MuiGrid>
      </MuiGrid>
    </MdmOfficeSearchResultWrapper>
  );
};
